import { Link, router } from '@inertiajs/react'
import { useState } from 'react'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatVuu(cents) {
  if (!cents && cents !== 0) return '—'
  return `⬡ ${(cents / 100).toFixed(2)}`
}

function timeAgo(iso) {
  if (!iso) return '—'
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60)    return `${sec}s ago`
  if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  return `${Math.floor(sec / 86400)}d ago`
}

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

function isRecentlyActive(iso) {
  if (!iso) return false
  return (Date.now() - new Date(iso).getTime()) < 24 * 60 * 60 * 1000
}

// ─── Badges ──────────────────────────────────────────────────────────────────

const TYPE_STYLE = {
  standard: 'bg-blue-100 text-blue-800',
  mobile:   'bg-purple-100 text-purple-800',
  guest:    'bg-gray-100 text-gray-600',
  staff:    'bg-orange-100 text-orange-800',
  admin:    'bg-red-100 text-red-800',
}

function TypeBadge({ type }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${TYPE_STYLE[type] || 'bg-gray-100 text-gray-600'}`}>
      {type}
    </span>
  )
}

function StatusBadge({ status }) {
  const cls = status === 'active'
    ? 'bg-green-100 text-green-800'
    : status === 'suspended'
      ? 'bg-red-100 text-red-700'
      : 'bg-gray-100 text-gray-500'
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  )
}

// Guest expiry is a separate dimension from account status
function ExpiryBadge({ user }) {
  if (user.user_type !== 'guest' || !user.guest_expires_at) return <span className="text-gray-300">—</span>
  if (user.guest_expired) {
    return (
      <div>
        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">EXPIRED</span>
        <p className="text-gray-400 text-xs mt-0.5">{fmtDate(user.guest_expires_at)}</p>
      </div>
    )
  }
  return (
    <div>
      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-teal-50 text-teal-700">Valid</span>
      <p className="text-gray-400 text-xs mt-0.5">{fmtDate(user.guest_expires_at)}</p>
    </div>
  )
}

// ─── Temp password modal ──────────────────────────────────────────────────────

function TempPasswordModal({ data, onClose }) {
  const [copied, setCopied] = useState(false)
  if (!data) return null
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Temporary Password</h2>
        <p className="text-sm text-gray-500 mb-4">
          Share with <span className="font-medium">{data.email}</span> out-of-band. Not shown again.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-lg text-center tracking-widest text-gray-900 mb-4 select-all">
          {data.temp_password}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { navigator.clipboard?.writeText(data.temp_password); setCopied(true) }}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
          <button onClick={onClose}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Action cell ─────────────────────────────────────────────────────────────

function ActionCell({ user, onUpdate, onTempPassword }) {
  const [working, setWorking] = useState(false)

  async function post(path, body = {}) {
    setWorking(true)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res  = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    } finally {
      setWorking(false)
    }
  }

  const btn = 'px-2.5 py-1 rounded text-xs font-semibold transition-colors disabled:opacity-50'

  return (
    <div className="flex flex-wrap gap-1">
      {user.status === 'active' ? (
        <button onClick={async () => {
          if (!confirm(`Suspend ${user.email}?\n\nSuspended accounts cannot log in until reactivated.`)) return
          const json = await post(`/admin/users/${user.id}/suspend`)
          onUpdate(json.user)
        }} disabled={working}
          className={`${btn} bg-red-50 text-red-700 hover:bg-red-100 border border-red-200`}>
          Suspend
        </button>
      ) : (
        <button onClick={async () => {
          const json = await post(`/admin/users/${user.id}/activate`)
          onUpdate(json.user)
        }} disabled={working}
          className={`${btn} bg-green-50 text-green-700 hover:bg-green-100 border border-green-200`}>
          Activate
        </button>
      )}

      {user.user_type !== 'guest' && (
        <button onClick={async () => {
          if (!confirm(`Reset password for ${user.email}?\nA temp password will be generated.`)) return
          const json = await post(`/admin/users/${user.id}/reset_password`)
          onTempPassword(json)
        }} disabled={working}
          className={`${btn} bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200`}>
          Reset PW
        </button>
      )}
    </div>
  )
}

// ─── Filter pill ─────────────────────────────────────────────────────────────

function Pill({ active, children, onClick, color = 'blue' }) {
  const activeClass = {
    blue:   'bg-blue-600 text-white',
    green:  'bg-green-600 text-white',
    amber:  'bg-amber-500 text-white',
    red:    'bg-red-600 text-white',
    purple: 'bg-purple-600 text-white',
  }[color]
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
        active ? activeClass : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {children}
    </button>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Users({
  users: initialUsers = [],
  total = 0,
  page = 1,
  per_page = 100,
  total_with_sessions = 0,
  expired_guest_count = 0,
  filters = {},
  flash = {},
}) {
  const [users, setUsers]   = useState(initialUsers)
  const [search, setSearch] = useState(filters.q || '')
  const [tempPw, setTempPw] = useState(null)
  const [notice, setNotice] = useState(flash.notice || null)
  const [purging, setPurging] = useState(false)

  const activeSessionFilter = filters.active_session === '1'
  const expiredGuestsFilter = filters.expired_guests === '1'
  const recentActiveFilter  = filters.recent_active  === '1'
  const typeFilter          = filters.type   || ''
  const statusFilter        = filters.status || ''

  function buildUrl(overrides = {}) {
    const p = new URLSearchParams()
    const q             = overrides.q             ?? search
    const type          = overrides.type          ?? typeFilter
    const status        = overrides.status        ?? statusFilter
    const activeSession = overrides.active_session ?? (activeSessionFilter ? '1' : '')
    const expiredGuests = overrides.expired_guests ?? (expiredGuestsFilter ? '1' : '')
    const recentActive  = overrides.recent_active  ?? (recentActiveFilter  ? '1' : '')
    const pg            = overrides.page           ?? 1
    if (q)             p.set('q', q)
    if (type)          p.set('type', type)
    if (status)        p.set('status', status)
    if (activeSession) p.set('active_session', activeSession)
    if (expiredGuests) p.set('expired_guests', expiredGuests)
    if (recentActive)  p.set('recent_active', recentActive)
    if (pg > 1)        p.set('page', pg)
    return `/admin/users?${p.toString()}`
  }

  function handleUserUpdate(updatedUser) {
    setUsers(prev => prev.map(u => u.id === updatedUser.id ? { ...u, ...updatedUser } : u))
    setNotice('User updated.')
    setTimeout(() => setNotice(null), 3000)
  }

  async function handlePurgeExpiredGuests() {
    if (!confirm(`Permanently delete ${expired_guest_count} expired guest account(s)?\n\nAccounts with active sessions are protected and will not be deleted.`)) return
    setPurging(true)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res  = await fetch('/admin/users/purge_expired_guests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      })
      const json = await res.json()
      setNotice(json.message)
      setTimeout(() => router.reload({ only: ['users', 'total', 'expired_guest_count'] }), 800)
    } finally {
      setPurging(false)
    }
  }

  const TYPE_TABS   = ['', 'standard', 'mobile', 'guest', 'admin', 'staff']
  const STATUS_TABS = ['', 'active', 'suspended']
  const totalPages  = Math.ceil(total / per_page)

  return (
    <div className="min-h-screen bg-gray-50">
      <TempPasswordModal data={tempPw} onClose={() => setTempPw(null)} />

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">

          {/* Top row */}
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">
                <Link href="/admin/queue_monitor" className="text-gray-500 hover:text-gray-700">← Queue Monitor</Link>
                {' · '}
                <Link href="/admin/menus" className="text-gray-500 hover:text-gray-700">Menu Catalog</Link>
              </p>
              <h1 className="text-2xl font-bold text-gray-900">User Registry</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                All accounts ever created &nbsp;·&nbsp;
                <span className="text-green-700 font-medium">{total_with_sessions} with active queue session{total_with_sessions !== 1 ? 's' : ''}</span>
                {expired_guest_count > 0 && (
                  <>&nbsp;·&nbsp;<span className="text-amber-600 font-medium">{expired_guest_count} expired guests</span></>
                )}
              </p>
            </div>

            {/* Search + purge */}
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && router.visit(buildUrl({ q: search }))}
                placeholder="Search email, name, alias…"
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-52 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button onClick={() => router.visit(buildUrl({ q: search }))}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                Search
              </button>
              {expired_guest_count > 0 && (
                <button onClick={handlePurgeExpiredGuests} disabled={purging}
                  className="px-4 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50">
                  {purging ? 'Purging…' : `Purge ${expired_guest_count} expired guests`}
                </button>
              )}
            </div>
          </div>

          {/* Notice */}
          {notice && (
            <div className="mt-3 bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-2 rounded-lg">
              {notice}
            </div>
          )}

          {/* ── Filter rows ── */}
          <div className="mt-3 space-y-2">

            {/* Row 1 — Account type */}
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-gray-400 font-medium w-14 shrink-0">Type:</span>
              {TYPE_TABS.map(t => (
                <Pill key={t || 'all'}
                  active={typeFilter === t && !activeSessionFilter && !expiredGuestsFilter && !recentActiveFilter}
                  color="blue"
                  onClick={() => router.visit(buildUrl({ type: t, active_session: '', expired_guests: '', recent_active: '' }))}
                >
                  {t || 'All'}
                </Pill>
              ))}
            </div>

            {/* Row 2 — Account status */}
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-gray-400 font-medium w-14 shrink-0">Status:</span>
              {STATUS_TABS.map(s => (
                <Pill key={s || 'all'}
                  active={statusFilter === s}
                  color={s === 'suspended' ? 'red' : 'blue'}
                  onClick={() => router.visit(buildUrl({ status: s }))}
                >
                  {s === ''          ? 'All'       : ''}
                  {s === 'active'    ? '✓ Active'   : ''}
                  {s === 'suspended' ? '⊘ Suspended' : ''}
                </Pill>
              ))}
            </div>

            {/* Row 3 — Special filters */}
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-gray-400 font-medium w-14 shrink-0">Show:</span>
              <Pill
                active={recentActiveFilter}
                color="green"
                onClick={() => router.visit(buildUrl({ recent_active: recentActiveFilter ? '' : '1', active_session: '', expired_guests: '' }))}
              >
                🟢 Active last 24 h
              </Pill>
              <Pill
                active={activeSessionFilter}
                color="purple"
                onClick={() => router.visit(buildUrl({ active_session: activeSessionFilter ? '' : '1', recent_active: '', expired_guests: '' }))}
              >
                ⚡ In Queue Session
              </Pill>
              <Pill
                active={expiredGuestsFilter}
                color="amber"
                onClick={() => router.visit(buildUrl({ expired_guests: expiredGuestsFilter ? '' : '1', recent_active: '', active_session: '' }))}
              >
                ⌛ Guest Window Expired
              </Pill>
            </div>
          </div>

          <p className="mt-2 text-xs text-gray-400">
            Showing {total} account{total !== 1 ? 's' : ''} · Page {page} of {totalPages || 1}
            &nbsp;·&nbsp;
            <span className="text-gray-500 italic">
              Account Status (Active/Suspended) and Guest Expiry are independent —
              a guest can be Status&nbsp;Active but have an Expired 24&nbsp;h window.
              "Active last 24 h" uses last login time as a proxy for network presence.
            </span>
          </p>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {users.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-4xl mb-3">👤</p>
            <p className="text-xl font-semibold text-gray-700">No accounts found</p>
            <p className="text-gray-500 mt-1">Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3 font-semibold">ID</th>
                    <th className="px-4 py-3 font-semibold">Email / Alias</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold" title="Has the account been manually suspended?">
                      Account Status
                    </th>
                    <th className="px-4 py-3 font-semibold" title="Guest 24h session window — independent of account status">
                      Guest Window
                    </th>
                    <th className="px-4 py-3 font-semibold" title="Last login timestamp — proxy for network activity">
                      Last Login
                    </th>
                    <th className="px-4 py-3 font-semibold" title="Active queue sessions right now (not app-open status)">
                      Queue Sessions
                    </th>
                    <th className="px-4 py-3 font-semibold">Wallet</th>
                    <th className="px-4 py-3 font-semibold">Joined</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => (
                    <tr key={u.id}
                      className={`hover:bg-gray-50 transition-colors ${u.status === 'suspended' ? 'opacity-50' : ''}`}>

                      <td className="px-4 py-3 font-mono text-xs text-gray-500">#{u.id}</td>

                      <td className="px-4 py-3 max-w-[220px]">
                        <p className="font-medium text-gray-900 truncate">{u.email}</p>
                        {u.display_name && u.display_name !== u.email && (
                          <p className="text-xs text-gray-500 truncate">{u.display_name}</p>
                        )}
                      </td>

                      <td className="px-4 py-3"><TypeBadge type={u.user_type} /></td>

                      {/* Account Status — manual admin control */}
                      <td className="px-4 py-3"><StatusBadge status={u.status} /></td>

                      {/* Guest Window — time-based expiry, independent of status */}
                      <td className="px-4 py-3"><ExpiryBadge user={u} /></td>

                      {/* Last Login — best proxy for network presence */}
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {u.last_login_at ? (
                          <>
                            <p className={isRecentlyActive(u.last_login_at) ? 'text-green-700 font-medium' : ''}>
                              {timeAgo(u.last_login_at)}
                            </p>
                            <p className="text-gray-400">{fmtDate(u.last_login_at)}</p>
                          </>
                        ) : <span className="text-gray-400">Never</span>}
                      </td>

                      {/* Queue Sessions */}
                      <td className="px-4 py-3 text-xs">
                        {(u.active_guest_sessions + u.active_creator_sessions) > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {u.active_guest_sessions > 0 && (
                              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                {u.active_guest_sessions} as guest
                              </span>
                            )}
                            {u.active_creator_sessions > 0 && (
                              <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium">
                                {u.active_creator_sessions} as creator
                              </span>
                            )}
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>

                      <td className="px-4 py-3 text-xs">
                        <p className="font-medium text-gray-800">{formatVuu(u.spendable_vuu_cents)}</p>
                        {u.locked_vuu_cents > 0 && (
                          <p className="text-gray-400">{formatVuu(u.locked_vuu_cents)} locked</p>
                        )}
                      </td>

                      <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(u.created_at)}</td>

                      <td className="px-4 py-3">
                        <ActionCell user={u} onUpdate={handleUserUpdate} onTempPassword={setTempPw} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between bg-gray-50">
                <p className="text-sm text-gray-500">
                  {(page - 1) * per_page + 1}–{Math.min(page * per_page, total)} of {total}
                </p>
                <div className="flex gap-2">
                  {page > 1 && (
                    <button onClick={() => router.visit(buildUrl({ page: page - 1 }))}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100">
                      ← Prev
                    </button>
                  )}
                  {page < totalPages && (
                    <button onClick={() => router.visit(buildUrl({ page: page + 1 }))}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100">
                      Next →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
